import { mediaTypeOf } from "@rally/agreed";
import { useCallback, useEffect, useRef, useState } from "react";
import { failureSaid } from "../../localization/failure.ts";
import { say } from "../../localization/say.ts";
import type { TakenUpload } from "../../net/api.ts";
import { climbing } from "../../net/rally.ts";
import { sizeSaid, tooBig, tooBigSaid } from "./filing.ts";

export type Picked = {
  key: string;
  name: string;

  mediaType: string;
  bytes: number;

  sent: number;

  preview: string | null;
  state: "climbing" | "there" | "failed";

  why: string | null;

  taken: TakenUpload | null;
};

export function farAlong(one: Pick<Picked, "bytes" | "sent" | "state">): number {
  if (one.state === "there") return 1;
  if (one.bytes <= 0) return 1;
  return Math.max(0, Math.min(1, one.sent / one.bytes));
}

export function tileSaid(one: Picked): string {
  if (one.state === "climbing") return `${Math.round(farAlong(one) * 100)}%`;
  return sizeSaid(one.bytes);
}

export function stillClimbing(picked: readonly Picked[]): boolean {
  return picked.some((one) => one.state === "climbing");
}

export function whatLanded(picked: readonly Picked[]): Picked[] {
  return picked.filter((one) => one.state === "there" && one.taken !== null);
}

export type Attaching = {
  picked: Picked[];

  take: (files: readonly File[]) => void;

  drop: (key: string) => void;

  ready: () => Promise<Attachment[]>;

  taken: (files: readonly Attachment[]) => void;

  retry: (files: readonly Attachment[]) => void;
};

export type Attachment = { pick: string; key: string; name: string; mediaType: string };

function filesOn(carried: DataTransfer | null): File[] {
  return Array.from(carried?.files ?? []);
}

export function usePasted(take: (files: readonly File[]) => void): void {
  useEffect(() => {
    const pasted = (event: ClipboardEvent) => {
      const files = filesOn(event.clipboardData);
      if (files.length === 0) return;

      event.preventDefault();
      take(files);
    };
    document.addEventListener("paste", pasted);
    return () => document.removeEventListener("paste", pasted);
  }, [take]);
}

export function useDropped(take: (files: readonly File[]) => void): {
  onNode: (node: unknown) => void;
  dropping: boolean;
} {
  const [dropping, setDropping] = useState(false);

  const [element, setElement] = useState<HTMLElement | null>(null);
  const taking = useRef(take);
  taking.current = take;

  const onNode = useCallback((node: unknown) => setElement((node as HTMLElement | null) ?? null), []);

  useEffect(() => {
    if (!element) return;
    const carrying = (event: DragEvent) => Array.from(event.dataTransfer?.types ?? []).includes("Files");
    const over = (event: DragEvent) => {
      if (!carrying(event)) return;

      event.preventDefault();
      setDropping(true);
    };
    const left = () => setDropping(false);
    const dropped = (event: DragEvent) => {
      const files = filesOn(event.dataTransfer);
      setDropping(false);
      if (files.length === 0) return;
      event.preventDefault();
      taking.current(files);
    };
    element.addEventListener("dragover", over);
    element.addEventListener("dragleave", left);
    element.addEventListener("drop", dropped);
    return () => {
      element.removeEventListener("dragover", over);
      element.removeEventListener("dragleave", left);
      element.removeEventListener("drop", dropped);
    };
  }, [element]);

  return { onNode, dropping };
}

function whyOf(cause: unknown): string {
  if (cause instanceof Error) return failureSaid(cause, say("files.broke"));
  return say("files.broke");
}

export function useAttaching(conversation: string): Attaching {
  const [, setPicked] = useState<Picked[]>([]);

  const held = useRef<Picked[]>([]);

  const stops = useRef(new Map<string, () => void>());

  const sending = useRef(new Set<string>());

  const room = useRef(conversation);

  const discarded = useRef<Picked[]>([]);
  if (room.current !== conversation) {
    discarded.current.push(...held.current);
    held.current = [];
    sending.current.clear();
    room.current = conversation;
  }

  const write = useCallback((how: (were: Picked[]) => Picked[]) => {
    held.current = how(held.current);
    setPicked(held.current);
  }, []);

  useEffect(() => {
    if (room.current !== conversation) return;
    const gone = discarded.current.splice(0);
    for (const one of gone) {
      stops.current.get(one.key)?.();
      stops.current.delete(one.key);
      if (one.preview) URL.revokeObjectURL(one.preview);
    }
    setPicked(held.current);
  }, [conversation]);

  useEffect(
    () => () => {
      for (const stop of stops.current.values()) stop();
      stops.current.clear();
      for (const one of [...held.current, ...discarded.current]) {
        if (one.preview) URL.revokeObjectURL(one.preview);
      }
      held.current = [];
      discarded.current = [];
      sending.current.clear();
      room.current = "";
    },
    [],
  );

  const change = useCallback(
    (key: string, how: (was: Picked) => Picked) => {
      write((were) => were.map((one) => (one.key === key ? how(one) : one)));
    },
    [write],
  );

  const take = useCallback(
    (files: readonly File[]) => {
      const here = room.current;
      for (const file of files) {
        const key = `pick-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
        const mediaType = mediaTypeOf(file.type, file.name);
        const preview = mediaType.startsWith("image/") ? URL.createObjectURL(file) : null;
        const tile: Picked = {
          key,
          name: file.name,
          mediaType,
          bytes: file.size,
          sent: 0,
          preview,
          state: tooBig(file.size) ? "failed" : "climbing",
          why: tooBig(file.size) ? tooBigSaid(file.size) : null,
          taken: null,
        };
        write((were) => [...were, tile]);

        if (tile.state === "failed") continue;

        void climbing<TakenUpload>(
          "file.upload",
          { conversation: here },
          file,
          (sent) => change(key, (was) => (was.state === "climbing" ? { ...was, sent } : was)),
          (stop) => stops.current.set(key, stop),
        )
          .then((taken) => {
            if (room.current !== here) return;
            change(key, (was) => ({ ...was, state: "there", sent: was.bytes, taken, why: null }));
          })
          .catch((cause: unknown) => {
            if (room.current !== here) return;
            change(key, (was) => ({ ...was, state: "failed", why: whyOf(cause) }));
          })
          .finally(() => stops.current.delete(key));
      }
    },
    [change, write],
  );

  const drop = useCallback(
    (key: string) => {
      stops.current.get(key)?.();
      stops.current.delete(key);
      write((were) => {
        const going = were.find((one) => one.key === key);
        if (going?.preview) URL.revokeObjectURL(going.preview);
        return were.filter((one) => one.key !== key);
      });
    },
    [write],
  );

  const ready = useCallback(async (): Promise<Attachment[]> => {
    const here = room.current;

    const chosen = held.current.filter((one) => !sending.current.has(one.key)).map((one) => one.key);
    for (const key of chosen) sending.current.add(key);
    const belongs = (one: Picked) => chosen.includes(one.key);
    while (held.current.some((one) => belongs(one) && one.state === "climbing")) {
      await new Promise((done) => setTimeout(done, 80));
    }
    if (room.current !== here) {
      for (const key of chosen) sending.current.delete(key);
      return [];
    }
    const landed = whatLanded(held.current.filter(belongs));
    const landedKeys = new Set(landed.map((one) => one.key));
    for (const key of chosen) if (!landedKeys.has(key)) sending.current.delete(key);
    return landed.map((one) => ({
      pick: one.key,
      key: one.taken?.key ?? "",
      name: one.name,
      mediaType: one.mediaType,
    }));
  }, []);

  const retry = useCallback((files: readonly Attachment[]) => {
    for (const one of files) sending.current.delete(one.pick);
  }, []);

  const taken = useCallback(
    (files: readonly Attachment[]) => {
      for (const one of files) {
        sending.current.delete(one.pick);
        drop(one.pick);
      }
    },
    [drop],
  );

  return { picked: held.current, take, drop, ready, taken, retry };
}
